import {
    Button,
    CircularProgress,
    createStyles,
    Grid,
    Input,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import "moment-timezone";
import React, { ChangeEvent, MouseEvent } from "react";
import Moment from "react-moment";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IParcelBidLog } from "../../../models/parcel/IParcelBidLog";
import { LoadState } from "../../../models/utility/LoadState";
import { IAppDispatch, IAppState } from "../../../store";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import CountdownTimer from "../timers/CountdownTimer";
import Constants from "./../../../models/utility/Constants";
import ExportBidLogCSVButton from "./ExportBidLogCSVButton";
import LoaderButton from "../LoaderButton";

type ParcelBidLogListProps = ParcelBidStore.IParcelBidStoreState &
    typeof ParcelBidStore.actionCreators &
    WithStyles<typeof styles> & {
        auctionClosed: boolean;
        auctionEndDate: string;
        parcelId: string;
        parcelName: string;
        auctionName: string;
        startingBid: number;
        lastAcceptedBidOverride?: number;
        onUpdateClicked: (value: number | null) => void;
        isSaving: boolean;
    };

interface IParcelBidLogListState {
    page: number;
    rowsPerPage: number;
    isOverridingFinalBid: boolean;
    overrideFinalBidValue: number;
}
const BlackCountdownTimer = withStyles({
    digit: {
        color: "#000"
    },
    digitCaption: {
        color: "#000"
    },
    digitColon: {
        color: "#000"
    }
})(CountdownTimer);

const styles = (theme: Theme) =>
    createStyles({
        countdownWrapper: {
            display: "inline-block",
            paddingLeft: theme.spacing(),
            width: 150
        },
        currentBid: { color: theme.palette.success.main },
        exportButtonWrapper: {
            alignItems: "center",
            display: "flex"
        },
        finalBid: {
            color: theme.palette.primary.main,
            maxWidth: theme.spacing(16),
            marginRight: theme.spacing()
        },
        button: { marginLeft: theme.spacing() },
        loaderWrapper: {
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            margin: theme.spacing(5),
            width: "100%"
        },
        table: { width: "100%" },
        tableCell: { borderColor: theme.palette.divider },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        tableLast: { marginBottom: 0 },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            borderColor: theme.palette.grey[500]
        },
        tableRowDeclined: {
            backgroundColor: `${theme.palette.primary.light} !important`,
            borderColor: `${theme.palette.primary.main} !important`
        },
        tableRowHighest: {
            backgroundColor: `${theme.palette.success.light} !important`,
            borderColor: `${theme.palette.success.main} !important`
        },
        timeLeft: {
            display: "inline-block",
            marginTop: theme.spacing()
        }
    });

class ParcelBidLogList extends React.PureComponent<
    ParcelBidLogListProps,
    IParcelBidLogListState
> {
    constructor(props: ParcelBidLogListProps) {
        super(props);

        this.state = {
            page: 0,
            rowsPerPage: 10,
            isOverridingFinalBid: false,
            overrideFinalBidValue:
                props.lastAcceptedBidOverride ??
                props.parcelAdminBidLogs
                    .find((e) => e.parcelId === props.parcelId)
                    ?.logs.find((e) => e.isAccepted)?.bidAmount ??
                props.startingBid
        };
    }

    public componentWillMount() {
        const parcelLogState = this.props.parcelAdminBidLogs.find(
            (e) => e.parcelId === this.props.parcelId
        );

        if (
            !parcelLogState ||
            parcelLogState.loadState === LoadState.NotLoaded
        ) {
            this.props.loadParcelAdminBidLogs(this.props.parcelId);
        }
    }

    public componentWillUnmount() {
        this.props.toggleParcelAdminBidLogLoadState(
            this.props.parcelId,
            LoadState.NotLoaded
        );
    }

    public componentDidUpdate(
        prevProps: Readonly<ParcelBidLogListProps>
    ): void {
        if (prevProps.isSaving && !this.props.isSaving) {
            this.setState({ ...this.state, isOverridingFinalBid: false });
        }
        if (
            prevProps.parcelAdminBidLogs.find(
                (e) => e.parcelId === this.props.parcelId
            ) !==
            this.props.parcelAdminBidLogs.find(
                (e) => e.parcelId === this.props.parcelId
            )
        ) {
            const overrideFinalBidValue =
                this.props.lastAcceptedBidOverride ??
                this.props.parcelAdminBidLogs
                    .find((e) => e.parcelId === this.props.parcelId)
                    ?.logs.find((e) => e.isAccepted)?.bidAmount ??
                this.props.startingBid;
            this.setState({ ...this.state, overrideFinalBidValue });
        }
    }

    public render() {
        const classes = this.props.classes;
        const parcelLogState = this.props.parcelAdminBidLogs.find(
            (e) => e.parcelId === this.props.parcelId
        );

        if (!parcelLogState || parcelLogState.loadState !== LoadState.Loaded) {
            return (
                <div className={classes.loaderWrapper}>
                    <CircularProgress color="secondary" />
                </div>
            );
        }

        let currentBid = this.props.startingBid;

        if (this.state.isOverridingFinalBid) {
            currentBid = this.state.overrideFinalBidValue;
        } else if (this.props.lastAcceptedBidOverride) {
            currentBid = this.props.lastAcceptedBidOverride;
        } else if (parcelLogState.logs && parcelLogState.logs.length > 0) {
            const firstAccepted = parcelLogState.logs.find((e) => e.isAccepted);
            if (firstAccepted) {
                currentBid = firstAccepted.bidAmount;
            }
        }

        const topBidId =
            parcelLogState.logs && parcelLogState.logs.length > 0
                ? parcelLogState.logs[0].parcelBidId
                : "";

        return (
            <div>
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={classes.tableCellHeader}
                                colSpan={
                                    this.props.lastAcceptedBidOverride ? 2 : 3
                                }
                            >
                                <Typography
                                    className={classes.tableHeader}
                                    variant="subtitle1"
                                >
                                    Parcel Bid History
                                </Typography>
                            </TableCell>
                            {!this.props.auctionClosed && (
                                <TableCell
                                    className={classes.tableCellHeader}
                                    colSpan={2}
                                >
                                    <Typography
                                        className={classes.tableHeader}
                                        variant="subtitle1"
                                    >
                                        <span className={classes.timeLeft}>
                                            Time Left:&nbsp;
                                        </span>
                                        <div
                                            className={classes.countdownWrapper}
                                        >
                                            <BlackCountdownTimer
                                                entityId={this.props.parcelId}
                                                date={this.props.auctionEndDate}
                                                hideDayHourZeroes={true}
                                                useCircleLoader={false}
                                            />
                                        </div>
                                    </Typography>
                                </TableCell>
                            )}
                            <TableCell
                                className={classes.tableCellHeader}
                                colSpan={this.props.auctionClosed ? 4 : 2}
                            >
                                <Typography
                                    className={classes.tableHeader}
                                    variant="subtitle1"
                                >
                                    {this.props.auctionClosed
                                        ? this.props.lastAcceptedBidOverride !==
                                              undefined ||
                                          this.state.isOverridingFinalBid
                                            ? "Final Sale Price"
                                            : "Final Bid"
                                        : "Current Bid"}
                                    :&nbsp;
                                    <NumberFormat
                                        className={
                                            this.props.auctionClosed
                                                ? classes.finalBid
                                                : classes.currentBid
                                        }
                                        value={currentBid}
                                        onValueChange={(values) =>
                                            this.setState({
                                                ...this.state,
                                                overrideFinalBidValue:
                                                    values.floatValue ?? 0
                                            })
                                        }
                                        allowNegative={false}
                                        customInput={(props) => {
                                            return (
                                                <Input
                                                    classes={{
                                                        input: classes.finalBid
                                                    }}
                                                    color="primary"
                                                    autoFocus={true}
                                                    {...props}
                                                />
                                            );
                                        }}
                                        displayType={
                                            this.state.isOverridingFinalBid
                                                ? "input"
                                                : "text"
                                        }
                                        thousandSeparator={true}
                                        prefix="$"
                                        decimalScale={0}
                                        fixedDecimalScale={true}
                                        allowLeadingZeros={false}
                                    />
                                    {this.props.auctionClosed && (
                                        <LoaderButton
                                            loading={this.props.isSaving}
                                            variant="contained"
                                            onClick={this.onOverrideClicked}
                                            className={classes.button}
                                        >
                                            {this.state.isOverridingFinalBid
                                                ? "Update"
                                                : "Override Final Bid"}
                                        </LoaderButton>
                                    )}
                                    {this.props.auctionClosed &&
                                        this.props.lastAcceptedBidOverride && (
                                            <LoaderButton
                                                loading={this.props.isSaving}
                                                variant="contained"
                                                color="secondary"
                                                onClick={
                                                    this.onClearOverrideClicked
                                                }
                                                className={classes.button}
                                            >
                                                Clear Override
                                            </LoaderButton>
                                        )}
                                    {this.state.isOverridingFinalBid && (
                                        <Button
                                            variant="contained"
                                            onClick={() =>
                                                this.setState({
                                                    ...this.state,
                                                    isOverridingFinalBid: false
                                                })
                                            }
                                            color="secondary"
                                            className={classes.button}
                                            disabled={this.props.isSaving}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                            >
                                Received On
                            </TableCell>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                            >
                                Bid Amount
                            </TableCell>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                            >
                                State
                            </TableCell>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                            >
                                Bidder Name
                            </TableCell>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                            >
                                Bidder Email
                            </TableCell>
                            <TableCell
                                className={classes.tableCell}
                                size="small"
                            >
                                Bidder Phone
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(!parcelLogState.logs ||
                            parcelLogState.logs.length === 0) && (
                            <TableRow>
                                <TableCell
                                    className={classes.tableCell}
                                    size="small"
                                    colSpan={6}
                                >
                                    <Typography
                                        component="div"
                                        variant="body2"
                                        align="center"
                                        color="textSecondary"
                                    >
                                        No bid logs to display at this time.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {parcelLogState.logs &&
                            parcelLogState.logs.length > 0 &&
                            parcelLogState.logs
                                .slice(
                                    this.state.page * this.state.rowsPerPage,
                                    this.state.page * this.state.rowsPerPage +
                                        this.state.rowsPerPage
                                )
                                .map((parcelBid) =>
                                    this.renderBidRow(parcelBid, topBidId)
                                )}
                    </TableBody>
                </Table>

                <Grid container={true} spacing={4}>
                    <Grid
                        item={true}
                        xs={2}
                        className={classes.exportButtonWrapper}
                    >
                        <ExportBidLogCSVButton
                            parcelId={this.props.parcelId}
                            parcelName={this.props.parcelName}
                            auctionName={this.props.auctionName}
                        />
                    </Grid>
                    <Grid item={true} xs={10}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            component="div"
                            count={parcelLogState.logs.length}
                            rowsPerPage={this.state.rowsPerPage}
                            page={this.state.page}
                            backIconButtonProps={{
                                "aria-label": "Previous Page"
                            }}
                            nextIconButtonProps={{ "aria-label": "Next Page" }}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                        />
                    </Grid>
                </Grid>
            </div>
        );
    }

    private renderBidRow = (bid: IParcelBidLog, topBidId: string) => {
        const classes = this.props.classes;
        let rowClasses = classes.tableRow;

        if (!bid.isAccepted) {
            rowClasses += ` ${classes.tableRowDeclined}`;
        } else if (this.props.auctionClosed && bid.parcelBidId === topBidId) {
            rowClasses += ` ${classes.tableRowHighest}`;
        }

        return (
            <TableRow key={bid.parcelBidId} className={rowClasses}>
                <TableCell className={classes.tableCell} size="small">
                    <Moment
                        date={bid.bidReceivedTime}
                        format={Constants.DateFormatBidLog}
                        tz={Constants.TimeZone}
                    />
                </TableCell>
                <TableCell className={classes.tableCell} size="small">
                    <NumberFormat
                        value={bid.bidAmount}
                        thousandSeparator={true}
                        prefix="$"
                        decimalScale={0}
                        fixedDecimalScale={true}
                        displayType="text"
                    />
                </TableCell>
                <TableCell className={classes.tableCell} size="small">
                    {bid.resolutionState}
                </TableCell>
                <TableCell className={classes.tableCell} size="small">
                    {bid.fullName}
                </TableCell>
                <TableCell className={classes.tableCell} size="small">
                    {bid.email}
                </TableCell>
                <TableCell className={classes.tableCell} size="small">
                    {bid.phoneNumber}
                </TableCell>
            </TableRow>
        );
    };

    private handleChangePage = (
        event: MouseEvent<HTMLButtonElement> | null,
        page: number
    ) => {
        this.setState({ page });
    };

    private handleChangeRowsPerPage = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const rowsPerPage = +event.target.value;
        this.setState({ rowsPerPage });
    };

    private onOverrideClicked = () => {
        if (!this.state.isOverridingFinalBid) {
            const shouldOverride = window.confirm(
                "Are you sure you want to override the final bid price? This should only be done if the final sale price was different from the final bid amount."
            );
            this.setState({
                ...this.state,
                isOverridingFinalBid: shouldOverride
            });
        } else {
            this.props.onUpdateClicked(this.state.overrideFinalBidValue);
        }
    };

    private onClearOverrideClicked = () => {
        const shouldClear = window.confirm(
            "Are you sure you want to clear the final sale price? The displayed price will return to the value of the last accepted bid."
        );
        if (shouldClear) {
            this.props.onUpdateClicked(null);
        }
    };
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.parcelBidStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign({}, ParcelBidStore.actionCreators),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ParcelBidLogList)
);
